import { useEffect, useRef, useState } from "react";
import Moveable from "react-moveable";
import {
  Box,
  Checkbox,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
  Typography,
} from "@mui/material";
import { Delete } from "@mui/icons-material";

import { SettingDialog } from "../SettingDialog";
import { MapViewer } from "../../../Form/MapViewer";
import Button from "../../../Button/Button";
import { useWindowResize } from "../../../../hooks/useWindowResize";
import { CleaningMapItem } from "types/response/map";
import { CleaningEventItem } from "types/response/cleaningEvent";
import { useUpdateMapMutation } from "hooks/worker/useMap";
import { handleError } from "utils/error/handleError";
import { notify } from "lib/notify";

interface SettingMapDialogProps {
  open: boolean;
  map: CleaningMapItem;
  latestLog?: CleaningEventItem;
  hideEnabledSetting?: boolean;
  hideMapDisplay?: boolean;
  onClickSave: () => void;
  onClickCancel: () => void;
}

export const SettingMapDialog = (props: SettingMapDialogProps) => {
  const [initialMap, setInitialMap] = useState<CleaningMapItem>(props.map);
  const [editingMap, setEditingMap] = useState<CleaningMapItem>(props.map);
  const updateMutation = useUpdateMapMutation();
  const [editable, setEditable] = useState(false);
  const ref = useRef<HTMLImageElement>(null);
  const [containerRef, setContainer] = useState<HTMLDivElement>();
  const moveableRef = useRef<Moveable>(null);
  const { windowWidth } = useWindowResize();

  const onClickSave = () => {
    if (!editingMap.mapName) {
      notify("マップ名を入力してください。", "error");
      return;
    }
    updateMutation.mutate(editingMap, {
      onError: (e) => handleError(e),
      onSuccess: () => {
        props.onClickSave();
      },
    });
  };

  useEffect(() => {
    if (!props.latestLog?.imagePath) return;

    setTimeout(() => {
      setEditable(true);
    }, 500);
  }, [props.latestLog?.imagePath, ref]);

  useEffect(() => {
    // 前回設定時と画面サイズが異なる場合、設定を調整する
    if (!containerRef) return;

    const clientRect = containerRef.getBoundingClientRect();

    const setMap = (prev: CleaningMapItem) => {
      if (
        !prev.displaySetting.baseWidth ||
        prev.displaySetting.baseWidth === clientRect.width
      ) {
        return prev;
      }

      const fixValue = prev.displaySetting.baseWidth / clientRect.width;

      return {
        ...prev,
        displaySetting: {
          ...prev.displaySetting,
          offset: {
            x: Math.round(prev.displaySetting.offset.x / fixValue),
            y: Math.round(prev.displaySetting.offset.y / fixValue),
          },
          baseWidth: clientRect.width,
          baseHeight: clientRect.height,
        },
      };
    };

    setEditingMap(setMap);
    setInitialMap(setMap);
  }, [windowWidth, containerRef]);

  return (
    <SettingDialog
      open={props.open}
      onClickClose={props.onClickCancel}
      onClickSave={onClickSave}
    >
      <DialogTitle>「{props.map.mapName}」の設定</DialogTitle>
      <DialogContent
        sx={{
          display: "grid",
          border: "1px gray solid",
          borderRadius: "4px",
          mt: 2,
          overflow: "hidden",
        }}
      >
        <TextField
          required
          label="マップ名"
          value={editingMap.mapName}
          onChange={(e) => 
            setEditingMap((prev) => ({ ...prev, mapName: e.target.value }))
          }
          sx={{ mt: 4 }}
        ></TextField>

        <TextField
          label="説明"
          value={editingMap.description}
          onChange={(e) =>
            setEditingMap((prev) => ({ ...prev, description: e.target.value }))
          }
          minRows={3}
          multiline
          sx={{ mt: 2 }}
        />

        {!props.hideEnabledSetting && (
          <>
            <FormControlLabel
              control={
                <Checkbox
                  // defaultChecked={editingMap.enabled}
                  onChange={(e) =>
                    setEditingMap((prev) => ({
                      ...prev,
                      enabled: e.target.checked,
                    }))
                  }
                />
              }
              label="有効"
              labelPlacement="start"
              sx={{ mt: 2, ml: 0, justifyContent: "left" }}
            />
            <Typography variant={"caption"}>
              ※ 不要なマップを非表示にする場合に、チェックを外してください。
            </Typography>
          </>
        )}

        {!props.hideMapDisplay && (
          <Box sx={{ mt: 2, maxWidth: "400px" }}>
            <Box sx={{ display: "flex", alignItems: "flex-start", mb: 1 }}>
              <Box sx={{ display: "flex", flexFlow: "column", gap: 0.5 }}>
                <Typography color="primary" variant="caption">
                  {`大きさ：${editingMap.displaySetting.scale.toFixed(2)}`}
                </Typography>
                <Typography color="primary" variant="caption">
                  {`角度${editingMap.displaySetting.degree}`}°
                </Typography>
                <Typography color="primary" variant="caption">
                  {`横: ${editingMap.displaySetting.offset.x ?? 0}, 縦: ${
                    editingMap.displaySetting.offset.y ?? 0
                  }`}
                </Typography>
              </Box>
              <Button
                startIcon={<Delete />}
                variant="outlined"
                size="small"
                onClick={() => {
                  const setMap = (prev: CleaningMapItem) => {
                    return {
                      ...props.map,
                      displaySetting: {
                        degree: 0,
                        scale: 1,
                        offset: { x: 0, y: 0 },
                      },
                    };
                  };
                  setEditingMap(setMap);
                  setInitialMap(setMap);
                  ref.current?.style.setProperty("transform", "");
                  moveableRef.current?.moveable.checkUpdateRect();
                }}
                sx={{ ml: "auto", zIndex: 3001 }} // 設定UIがzIndex3000となっているので、それよりも上に表示する
              >
                <Typography variant="caption">リセット</Typography>
              </Button>
            </Box>

            <Box
              sx={{
                display: "grid",
                position: "relative",
                ".moveable-control": {
                  width: "26px !important",
                  height: "26px !important",
                  marginTop: "-13px !important",
                  marginLeft: "-13px !important",
                },
              }}
              ref={setContainer}
            >
              <Moveable
                ref={moveableRef}
                target={editable ? ref.current : undefined}
                draggable
                dragArea={true}
                originDraggable={true}
                originRelative={false}
                dragContainer={editable ? containerRef : undefined}
                container={editable ? containerRef : undefined}
                throttleDrag={1}
                onDrag={({ target, transform, translate, currentTarget }) => {
                  const containerRect = containerRef?.getBoundingClientRect();
                  if (!containerRect) return;

                  setEditingMap((prev) => ({
                    ...prev,
                    displaySetting: {
                      ...prev.displaySetting,
                      baseWidth: containerRect.width,
                      baseHeight: containerRect.height,
                      offset: {
                        x:
                          (initialMap.displaySetting.offset.x ?? 0) +
                          translate[0],
                        y:
                          (initialMap.displaySetting.offset.y ?? 0) +
                          translate[1],
                      },
                    },
                  }));
                  target.style.transform = transform;
                }}
                rotatable
                throttleRotate={1}
                transformOrigin={"center"}
                onRotate={({ target, transform, rotation }) => {
                  const rounded = Math.round(
                    initialMap.displaySetting.degree + rotation
                  );
                  const dest =
                    rounded >= 0 ? rounded % 360 : 360 + (rounded % 360);
                  const clientRect = containerRef?.getBoundingClientRect();

                  setEditingMap((prev) => ({
                    ...prev,
                    displaySetting: {
                      ...prev.displaySetting,
                      degree: dest,
                      baseWidth: clientRect?.width,
                      baseHeight: clientRect?.height,
                    },
                  }));
                  target.style.transform = transform;
                }}
                scalable
                keepRatio
                throttleScale={0.01}
                onScale={({ target, transform, scale }) => {
                  const clientRect = containerRef?.getBoundingClientRect();
                  setEditingMap((prev) => ({
                    ...prev,
                    displaySetting: {
                      ...prev.displaySetting,
                      scale: Number(
                        (initialMap.displaySetting.scale * scale[0]).toFixed(2)
                      ),
                      baseWidth: clientRect?.width,
                      baseHeight: clientRect?.height,
                    },
                  }));
                  target.style.transform = transform;
                }}
              />
              {props.latestLog?.imagePath && (
                <MapViewer
                  src={props.latestLog.imagePath}
                  displaySetting={editingMap.displaySetting}
                  mode="EDIT"
                  ref={ref}
                />
              )}
            </Box>
          </Box>
        )}
      </DialogContent>
    </SettingDialog>
  );
};
